<template>
  <div>
    <nav id="top" class="bg-white/95 backdrop-blur-md shadow-sm fixed w-full z-50 transition-all duration-300">
      <div class="xl:max-w-[70%] mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center h-20 sm:h-24 md:h-28">
          <div class="flex items-center flex-shrink-0 h-full py-3">
            <a class="h-full inline-flex items-center" aria-label="Deacon Marriner LLP Home" @click="scrollToTop()">
              <img src="/logo.webp" alt="Deacon Marriner LLP" class="h-full w-auto object-contain" loading="eager" />
            </a>
          </div>

          <div class="hidden md:flex items-center space-x-10 font-medium text-[15px]">
            <a href="#services" class="text-brand-dark hover:text-brand-green transition relative group py-2">
              Services<span class="absolute bottom-0 left-0 w-0 h-0.5 bg-brand-green transition-all group-hover:w-full"></span>
            </a>
            <a href="#about" class="text-brand-dark hover:text-brand-green transition relative group py-2">
              About<span class="absolute bottom-0 left-0 w-0 h-0.5 bg-brand-green transition-all group-hover:w-full"></span>
            </a>
            <a href="#projects" class="text-brand-dark hover:text-brand-green transition relative group py-2">
              Projects<span class="absolute bottom-0 left-0 w-0 h-0.5 bg-brand-green transition-all group-hover:w-full"></span>
            </a>
            <a href="#insights" class="text-brand-dark hover:text-brand-green transition relative group py-2">
              Insights<span class="absolute bottom-0 left-0 w-0 h-0.5 bg-brand-green transition-all group-hover:w-full"></span>
            </a>
            <a
              href="#contact"
              class="px-7 py-3 bg-brand-plum text-white rounded-md hover:bg-brand-plum/90 transition shadow-sm hover:shadow-md transform hover:-translate-y-0.5 font-bold text-sm tracking-wide uppercase"
            >
              Book Consultation
            </a>
          </div>

          <button
            class="md:hidden inline-flex items-center justify-center w-11 h-11 rounded-md border border-black/10 bg-white hover:bg-brand-grey transition"
            :aria-label="menuOpen ? 'Close menu' : 'Open menu'"
            aria-controls="mobileMenu"
            :aria-expanded="menuOpen ? 'true' : 'false'"
            type="button"
            @click="toggleMenu"
          >
            <svg v-if="!menuOpen" class="w-5 h-5 text-brand-darker" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M4 7h16M4 12h16M4 17h16"></path></svg>
            <svg v-else class="w-5 h-5 text-brand-darker" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"></path></svg>
          </button>
        </div>

        <div id="mobileMenu" class="md:hidden pb-6" v-show="menuOpen">
          <div class="mt-2 rounded-2xl bg-white shadow-soft-xl border border-black/5 overflow-hidden">
            <div class="p-3 flex flex-col gap-2">
              <a href="#services" class="px-4 py-3 rounded-xl hover:bg-brand-grey transition font-semibold" @click="closeMenu">Services</a>
              <a href="#about" class="px-4 py-3 rounded-xl hover:bg-brand-grey transition font-semibold" @click="closeMenu">About</a>
              <a href="#projects" class="px-4 py-3 rounded-xl hover:bg-brand-grey transition font-semibold" @click="closeMenu">Projects</a>
              <a href="#insights" class="px-4 py-3 rounded-xl hover:bg-brand-grey transition font-semibold" @click="closeMenu">Insights</a>
            </div>
            <div class="border-t border-black/5 p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
              <a
                href="#contact"
                class="block w-full px-4 py-3 rounded-xl bg-brand-plum text-white font-bold uppercase tracking-wide text-sm hover:bg-brand-plum/90 transition text-center"
                @click="closeMenu"
              >
                Book Consultation
              </a>
            </div>
          </div>
        </div>
      </div>
    </nav>

    <main id="main">
      <header
        class="relative pt-28 sm:pt-32 pb-12 sm:pb-12 lg:pt-44 lg:pb-16 flex items-center justify-center overflow-hidden"
        role="banner"
      >
        <picture>
          <source srcset="/bg-mobile.jpg" media="(max-width: 767px)" />
          <source srcset="/bg.webp" media="(min-width: 768px)" />
          <img
            src="/bg-mobile.jpg"
            alt=""
            aria-hidden="true"
            class="absolute inset-0 w-full h-full object-cover object-top hero-media"
            fetchpriority="high"
            loading="eager"
            decoding="async"
            width="900"
            height="1200"
          />
        </picture>
        <div class="absolute inset-0 hero-overlay pointer-events-none"></div>
        <div class="absolute inset-0 bg-black/10 pointer-events-none"></div>

        <div class="relative z-10 text-center px-4 sm:px-6 max-w-5xl mx-auto text-white opacity-0 animate-fade-in-up">
          <p class="sr-only">
            Deacon Marriner LLP provides specialist construction compliance advice across England and Wales, including CDM, building regulations, fire strategy support, and dutyholder guidance.
          </p>
          <div class="flex flex-wrap justify-center gap-2 sm:gap-3 mb-7 sm:mb-10 text-[10px] sm:text-xs font-bold tracking-widest uppercase">
            <span class="px-4 sm:px-5 py-2 rounded-full bg-brand-green/20 border border-brand-green/40 backdrop-blur-md shadow-glow">Principal Designer</span>
            <span class="px-4 sm:px-5 py-2 rounded-full bg-brand-green/20 border border-brand-green/40 backdrop-blur-md shadow-glow">CDM &amp; Building Regs</span>
            <span class="px-4 sm:px-5 py-2 rounded-full bg-brand-green/20 border border-brand-green/40 backdrop-blur-md shadow-glow">Golden Thread</span>
          </div>

          <h1 class="font-serif text-[42px] leading-[1.06] sm:text-5xl md:text-7xl font-black mb-5 sm:mb-7 tracking-tight drop-shadow-lg">
            <span class="block">Building Compliance</span>
            <span class="block mt-2 sm:mt-3">with Confidence</span>
          </h1>

          <p class="text-base sm:text-xl md:text-2xl text-gray-100 mb-8 sm:mb-12 max-w-3xl mx-auto leading-relaxed font-light">
            Specialist advice on safety, regulation, and dutyholder roles in England &amp; Wales. Helping construction professionals navigate the evolving safety regime.
          </p>

          <div class="flex flex-col sm:flex-row gap-3 sm:gap-5 justify-center mb-6">
            <a href="#contact" class="w-full sm:w-auto px-8 sm:px-10 py-4 bg-brand-green text-white font-bold rounded-xl shadow-lg hover:bg-[#7ab59c] transition transform hover:-translate-y-1">
              Book a Consultation
            </a>
            <a href="#services" class="w-full sm:w-auto px-8 sm:px-10 py-4 bg-white/95 text-brand-dark font-bold rounded-xl shadow-lg hover:bg-white transition transform hover:-translate-y-1">
              Explore Services
            </a>
          </div>

          <p class="text-white/80 text-xs sm:text-sm mt-4 flex items-center justify-center gap-2"><svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><rect x="5" y="11" width="14" height="9" rx="2"></rect><path d="M8 11V8a4 4 0 118 0v3"></path></svg><span>No spam. No pressure. Just clear next steps.</span></p>
        </div>
      </header>

      <section class="py-24 lg:py-28 bg-brand-grey">
        <div class="max-w-7xl xl:max-w-[70%] mx-auto px-4 sm:px-6 lg:px-8">
          <div class="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
            <div class="bg-white p-10 sm:p-12 rounded-3xl shadow-soft-xl relative overflow-hidden group">
              <div class="absolute top-0 left-0 w-3 h-full bg-brand-green group-hover:w-5 transition-all duration-500"></div>
              <h2 class="font-serif text-3xl md:text-4xl font-bold mb-8 text-brand-darker">Straight-talking safety and compliance advice</h2>
              <p class="text-brand-dark mb-10 text-lg leading-relaxed">
                We work with clients across the construction sector—contractors, developers, designers, and public bodies—providing expert support to ensure your projects stand on solid regulatory ground.
              </p>
              <div class="inline-flex items-center bg-brand-grey px-6 py-3 rounded-full text-brand-darker font-bold tracking-wide text-sm uppercase shadow-sm">
                <svg class="w-5 h-5 text-brand-green mr-3" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 22s7-6.2 7-12a7 7 0 10-14 0c0 5.8 7 12 7 12zm0-9.5A2.5 2.5 0 1112 7a2.5 2.5 0 010 5.5z"></path></svg> Based in Wales. Working nationally.
              </div>
            </div>

            <div class="pl-0 lg:pl-4">
              <ul class="space-y-4" aria-label="Key services highlight list">
                <li class="flex items-start p-4 -mx-4 rounded-xl hover:bg-white hover:shadow-sm transition-all duration-300 group cursor-default">
                  <div class="flex-shrink-0 w-12 h-12 rounded-full bg-white shadow-sm flex items-center justify-center mr-5 group-hover:bg-brand-green/10 transition">
                    <svg class="w-5 h-5 text-brand-green" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><path d="M5 13l4 4L19 7"></path></svg>
                  </div>
                  <span class="text-xl text-brand-darker font-medium pt-2">CDM 2015 Principal Designer services</span>
                </li>
                <li class="flex items-start p-4 -mx-4 rounded-xl hover:bg-white hover:shadow-sm transition-all duration-300 group cursor-default">
                  <div class="flex-shrink-0 w-12 h-12 rounded-full bg-white shadow-sm flex items-center justify-center mr-5 group-hover:bg-brand-green/10 transition">
                    <svg class="w-5 h-5 text-brand-green" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><path d="M5 13l4 4L19 7"></path></svg>
                  </div>
                  <span class="text-xl text-brand-darker font-medium pt-2">Building Safety Act (England) and emerging Welsh regulations</span>
                </li>
                <li class="flex items-start p-4 -mx-4 rounded-xl hover:bg-white hover:shadow-sm transition-all duration-300 group cursor-default">
                  <div class="flex-shrink-0 w-12 h-12 rounded-full bg-white shadow-sm flex items-center justify-center mr-5 group-hover:bg-brand-green/10 transition">
                    <svg class="w-5 h-5 text-brand-green" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><path d="M5 13l4 4L19 7"></path></svg>
                  </div>
                  <span class="text-xl text-brand-darker font-medium pt-2">Building control procedures and compliance</span>
                </li>
                <li class="flex items-start p-4 -mx-4 rounded-xl hover:bg-white hover:shadow-sm transition-all duration-300 group cursor-default">
                  <div class="flex-shrink-0 w-12 h-12 rounded-full bg-white shadow-sm flex items-center justify-center mr-5 group-hover:bg-brand-green/10 transition">
                    <svg class="w-5 h-5 text-brand-green" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><path d="M5 13l4 4L19 7"></path></svg>
                  </div>
                  <span class="text-xl text-brand-darker font-medium pt-2">Fire safety strategy and Golden Thread documentation</span>
                </li>
                <li class="flex items-start p-4 -mx-4 rounded-xl hover:bg-white hover:shadow-sm transition-all duration-300 group cursor-default">
                  <div class="flex-shrink-0 w-12 h-12 rounded-full bg-white shadow-sm flex items-center justify-center mr-5 group-hover:bg-brand-green/10 transition">
                    <svg class="w-5 h-5 text-brand-green" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><path d="M5 13l4 4L19 7"></path></svg>
                  </div>
                  <span class="text-xl text-brand-darker font-medium pt-2">Cross-border project compliance (England &amp; Wales)</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section class="py-20 lg:py-24 bg-brand-plum relative overflow-hidden" aria-label="Featured insight">
        <div class="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-brand-ash via-brand-plum to-[#5B415D]"></div>
        <div class="relative z-10 max-w-5xl mx-auto px-4 text-center text-white">
          <div class="inline-block p-6 rounded-full bg-white/10 mb-10 shadow-inner border border-white/20 backdrop-blur-md">
            <svg class="w-12 h-12 drop-shadow-lg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2l7 3v5c0 5-3.4 9.4-7 10-3.6-.6-7-5-7-10V5l7-3zm-1 6v4H8l4 5 4-5h-3V8h-2z"></path></svg>
          </div>
          <h2 class="font-serif text-4xl md:text-6xl font-black mb-7 tracking-tight leading-[1.05]">Preparing for the Welsh building safety regime</h2>
          <p class="text-xl md:text-3xl text-white/90 mb-12 max-w-4xl mx-auto font-light leading-relaxed">
            Significant changes are expected in Wales. Dutyholder roles, approval gateways, and robust digital records are moving from best practice to enforceable requirements.
          </p>
          <div class="bg-white/10 p-8 sm:p-10 rounded-3xl inline-block max-w-4xl mx-auto mb-10 backdrop-blur-md border border-white/30 shadow-2xl">
            <p class="font-bold mb-4 uppercase tracking-widest text-sm text-white/70 flex items-center justify-center">
              <span class="bg-brand-green text-white text-xs px-2 py-1 rounded mr-3">Featured</span>Insight
            </p>
            <a href="#insights" class="text-xl sm:text-2xl md:text-3xl font-bold hover:text-brand-green transition flex items-center justify-center gap-4 leading-tight">
              Read: Is there a Welsh equivalent to the Building Safety Act 2022?
              <span class="bg-white/20 rounded-full p-2 w-10 h-10 flex items-center justify-center" aria-hidden="true"><svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><path d="M5 12h14M13 6l6 6-6 6"></path></svg></span>
            </a>
          </div>
          <p class="font-semibold text-lg sm:text-xl text-white/90 tracking-wide">We support appointment decisions, compliance planning, and evidence management before enforcement action becomes a risk.</p>
        </div>
      </section>

      <section class="py-24 lg:py-28 bg-white" aria-label="Who we help">
        <div class="max-w-7xl xl:max-w-[70%] mx-auto px-4 sm:px-6 lg:px-8">
          <div class="text-center mb-16 lg:mb-20 max-w-4xl mx-auto">
            <h3 class="text-brand-green font-bold uppercase tracking-widest mb-6 text-sm">Our Clients</h3>
            <h2 class="font-serif text-5xl font-bold text-brand-darker mb-8">Who We Help</h2>
            <p class="text-3xl text-brand-plum font-serif italic leading-relaxed font-medium">“Whether you’re building in Birmingham or Bridgend—compliance is our foundation.”</p>
          </div>

          <div class="grid grid-cols-2 md:grid-cols-3 gap-8 lg:gap-10">
            <div class="bg-brand-grey p-9 rounded-3xl transition-all duration-300 text-center group hover:bg-white hover:shadow-soft-xl border-b-[6px] border-transparent hover:border-brand-green hover:-translate-y-2">
              <div class="w-20 h-20 mx-auto bg-white shadow-sm text-brand-green rounded-full flex items-center justify-center mb-8 group-hover:scale-110 transition duration-300 group-hover:bg-brand-green group-hover:text-white">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-10 h-10" aria-hidden="true"><path d="M4 14a8 8 0 0116 0"></path><path d="M3 14h18v5H3z"></path><path d="M12 6v8"></path></svg>
              </div>
              <h3 class="font-bold text-xl text-brand-darker tracking-tight">Principal Contractors</h3>
            </div>

            <div class="bg-brand-grey p-9 rounded-3xl transition-all duration-300 text-center group hover:bg-white hover:shadow-soft-xl border-b-[6px] border-transparent hover:border-brand-green hover:-translate-y-2">
              <div class="w-20 h-20 mx-auto bg-white shadow-sm text-brand-green rounded-full flex items-center justify-center mb-8 group-hover:scale-110 transition duration-300 group-hover:bg-brand-green group-hover:text-white">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-10 h-10" aria-hidden="true"><path d="M14.5 9.5l-2 5-5 2 2-5 5-2z"></path><path d="M12 3a9 9 0 100 18 9 9 0 000-18z"></path></svg>
              </div>
              <h3 class="font-bold text-xl text-brand-darker tracking-tight">Designers &amp; Architects</h3>
            </div>

            <div class="bg-brand-grey p-9 rounded-3xl transition-all duration-300 text-center group hover:bg-white hover:shadow-soft-xl border-b-[6px] border-transparent hover:border-brand-green hover:-translate-y-2">
              <div class="w-20 h-20 mx-auto bg-white shadow-sm text-brand-green rounded-full flex items-center justify-center mb-8 group-hover:scale-110 transition duration-300 group-hover:bg-brand-green group-hover:text-white">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-10 h-10" aria-hidden="true"><path d="M4 21V7l8-4 8 4v14"></path><path d="M9 21v-4h6v4"></path><path d="M8 10h.01"></path><path d="M12 10h.01"></path><path d="M16 10h.01"></path><path d="M8 14h.01"></path><path d="M12 14h.01"></path><path d="M16 14h.01"></path></svg>
              </div>
              <h3 class="font-bold text-xl text-brand-darker tracking-tight">Developers &amp; Clients</h3>
            </div>

            <div class="bg-brand-grey p-9 rounded-3xl transition-all duration-300 text-center group hover:bg-white hover:shadow-soft-xl border-b-[6px] border-transparent hover:border-brand-green hover:-translate-y-2">
              <div class="w-20 h-20 mx-auto bg-white shadow-sm text-brand-green rounded-full flex items-center justify-center mb-8 group-hover:scale-110 transition duration-300 group-hover:bg-brand-green group-hover:text-white">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-10 h-10" aria-hidden="true"><path d="M3 11l9-7 9 7"></path><path d="M5 10v10h14V10"></path><path d="M10 20v-6h4v6"></path></svg>
              </div>
              <h3 class="font-bold text-xl text-brand-darker tracking-tight">Housing Associations</h3>
            </div>

            <div class="bg-brand-grey p-9 rounded-3xl transition-all duration-300 text-center group hover:bg-white hover:shadow-soft-xl border-b-[6px] border-transparent hover:border-brand-green hover:-translate-y-2">
              <div class="w-20 h-20 mx-auto bg-white shadow-sm text-brand-green rounded-full flex items-center justify-center mb-8 group-hover:scale-110 transition duration-300 group-hover:bg-brand-green group-hover:text-white">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-10 h-10" aria-hidden="true"><path d="M3 10h18"></path><path d="M5 10V7l7-3 7 3v3"></path><path d="M6 10v8"></path><path d="M10 10v8"></path><path d="M14 10v8"></path><path d="M18 10v8"></path><path d="M3 20h18"></path></svg>
              </div>
              <h3 class="font-bold text-xl text-brand-darker tracking-tight">Local Authorities</h3>
            </div>

            <div class="bg-brand-grey p-9 rounded-3xl transition-all duration-300 text-center group hover:bg-white hover:shadow-soft-xl border-b-[6px] border-transparent hover:border-brand-green hover:-translate-y-2">
              <div class="w-20 h-20 mx-auto bg-white shadow-sm text-brand-green rounded-full flex items-center justify-center mb-8 group-hover:scale-110 transition duration-300 group-hover:bg-brand-green group-hover:text-white">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-10 h-10" aria-hidden="true"><path d="M8 3h7l4 4v14H8z"></path><path d="M15 3v4h4"></path><path d="M11 15l2 2 4-4"></path></svg>
              </div>
              <h3 class="font-bold text-xl text-brand-darker tracking-tight">Approved Inspectors</h3>
            </div>
          </div>
        </div>
      </section>

      <section id="services" class="py-24 lg:py-28 bg-brand-grey" aria-label="Services">
        <div class="max-w-7xl xl:max-w-[70%] mx-auto px-4 sm:px-6 lg:px-8">
          <div class="text-center mb-16 lg:mb-20">
            <h3 class="text-brand-green font-bold uppercase tracking-widest mb-6 text-sm">What We Offer</h3>
            <h2 class="font-serif text-5xl font-bold text-brand-darker">Our Expertise</h2>
          </div>

          <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
            <div class="bg-white p-10 rounded-3xl shadow-sm hover:shadow-soft-xl hover:-translate-y-2 transition duration-300 border-t-[6px] border-brand-green group">
              <div class="w-16 h-16 bg-brand-green/10 rounded-2xl flex items-center justify-center mb-8 text-brand-green group-hover:rotate-6 transition duration-300 group-hover:bg-brand-green group-hover:text-white shadow-sm">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-8 h-8" aria-hidden="true"><path d="M4 20L20 4"></path><path d="M14 6l4 4"></path><path d="M8 12l2 2"></path><path d="M5 15l2 2"></path></svg>
              </div>
              <h3 class="font-bold text-2xl mb-5 text-brand-darker tracking-tight">Principal Designer (CDM)</h3>
              <p class="text-brand-dark leading-relaxed text-lg">Managing health and safety risks during the pre-construction phase under CDM 2015 regulations.</p>
            </div>

            <div class="bg-white p-10 rounded-3xl shadow-sm hover:shadow-soft-xl hover:-translate-y-2 transition duration-300 border-t-[6px] border-brand-plum group">
              <div class="w-16 h-16 bg-brand-plum/10 rounded-2xl flex items-center justify-center mb-8 text-brand-plum group-hover:rotate-6 transition duration-300 group-hover:bg-brand-plum group-hover:text-white shadow-sm">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-8 h-8" aria-hidden="true"><path d="M12 3l9 5-9 5-9-5 9-5z"></path><path d="M3 12l9 5 9-5"></path><path d="M3 16l9 5 9-5"></path></svg>
              </div>
              <h3 class="font-bold text-2xl mb-5 text-brand-darker tracking-tight">Principal Designer (BSA 2022)</h3>
              <p class="text-brand-dark leading-relaxed text-lg">Navigating the new Building Regulations dutyholder roles for compliance with the Building Safety Act.</p>
            </div>

            <div class="bg-white p-10 rounded-3xl shadow-sm hover:shadow-soft-xl hover:-translate-y-2 transition duration-300 border-t-[6px] border-blue-500 group">
              <div class="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mb-8 text-blue-600 group-hover:rotate-6 transition duration-300 group-hover:bg-blue-600 group-hover:text-white shadow-sm">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-8 h-8" aria-hidden="true"><path d="M8 3h7l4 4v14H8z"></path><path d="M15 3v4h4"></path></svg>
              </div>
              <h3 class="font-bold text-2xl mb-5 text-brand-darker tracking-tight">Compliance Documentation</h3>
              <p class="text-brand-dark leading-relaxed text-lg">Preparation for Gateway approvals, digital record keeping, and safety case reports.</p>
            </div>

            <div class="bg-white p-10 rounded-3xl shadow-sm hover:shadow-soft-xl hover:-translate-y-2 transition duration-300 border-t-[6px] border-red-500 group">
              <div class="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mb-8 text-red-600 group-hover:rotate-6 transition duration-300 group-hover:bg-red-600 group-hover:text-white shadow-sm">
                <svg viewBox="0 0 24 24" fill="currentColor" stroke="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-8 h-8" aria-hidden="true"><path d="M12 3s2 2.2 2 5.2c0 1.5-.8 2.5-1.7 3.6-.9 1-1.8 2.1-1.8 3.8A3.5 3.5 0 0014 19a4 4 0 004-4c0-5-4-8-6-12z"></path></svg>
              </div>
              <h3 class="font-bold text-2xl mb-5 text-brand-darker tracking-tight">Fire Strategy Support</h3>
              <p class="text-brand-dark leading-relaxed text-lg">Developing comprehensive fire strategies and ensuring the Golden Thread of information.</p>
            </div>

            <div class="bg-white p-10 rounded-3xl shadow-sm hover:shadow-soft-xl hover:-translate-y-2 transition duration-300 border-t-[6px] border-indigo-500 group">
              <div class="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mb-8 text-indigo-600 group-hover:rotate-6 transition duration-300 group-hover:bg-indigo-600 group-hover:text-white shadow-sm">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-8 h-8" aria-hidden="true"><path d="M14.5 9.5l-2 5-5 2 2-5 5-2z"></path><path d="M12 3a9 9 0 100 18 9 9 0 000-18z"></path></svg>
              </div>
              <h3 class="font-bold text-2xl mb-5 text-brand-darker tracking-tight">Cross-border Reviews</h3>
              <p class="text-brand-dark leading-relaxed text-lg">Reviewing portfolios for projects spanning England and Wales to ensure dual compliance.</p>
            </div>

            <div class="bg-white p-10 rounded-3xl shadow-sm hover:shadow-soft-xl hover:-translate-y-2 transition duration-300 border-t-[6px] border-yellow-500 group">
              <div class="w-16 h-16 bg-yellow-100 rounded-2xl flex items-center justify-center mb-8 text-yellow-600 group-hover:rotate-6 transition duration-300 group-hover:bg-yellow-600 group-hover:text-white shadow-sm">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-8 h-8" aria-hidden="true"><path d="M3 10l9-4 9 4-9 4-9-4z"></path><path d="M7 12v4c0 1.5 2.2 3 5 3s5-1.5 5-3v-4"></path><path d="M21 10v5"></path></svg>
              </div>
              <h3 class="font-bold text-2xl mb-5 text-brand-darker tracking-tight">CPD &amp; Training</h3>
              <p class="text-brand-dark leading-relaxed text-lg">Upskilling your team on the latest dutyholder responsibilities and regulatory updates.</p>
            </div>
          </div>

          <div class="text-center mt-16 lg:mt-20">
            <a href="#contact" class="inline-block px-10 py-5 border-2 border-brand-darker text-brand-darker font-bold rounded-md hover:bg-brand-darker hover:text-white transition uppercase tracking-widest text-sm shadow-sm hover:shadow-md">
              Request a Service Pack
            </a>
          </div>
        </div>
      </section>

      <section id="about" class="relative py-24 lg:py-28 bg-white" aria-label="About">
        <div class="absolute inset-0 pointer-events-none">
          <div class="absolute -top-28 -right-28 w-[34rem] h-[34rem] rounded-full bg-brand-green/10 blur-3xl"></div>
          <div class="absolute -bottom-32 -left-32 w-[38rem] h-[38rem] rounded-full bg-brand-plum/10 blur-3xl"></div>
        </div>

        <div class="relative max-w-7xl xl:max-w-[70%] mx-auto px-4 sm:px-6 lg:px-8">
          <div class="max-w-4xl mx-auto text-center mb-14 lg:mb-16">
            <h3 class="text-brand-green font-bold uppercase tracking-widest mb-5 text-sm">About Deacon Marriner LLP</h3>

            <h2 class="font-serif text-5xl md:text-6xl font-black text-brand-darker leading-tight">
              Construction compliance—without the jargon.
            </h2>

            <p class="mt-6 text-xl sm:text-2xl text-brand-dark leading-relaxed font-light">
              We combine technical CDM and compliance expertise with professional back-office support—so every project is delivered safely,
              competently, and with the clarity our clients expect.
            </p>
          </div>

          <div class="grid lg:grid-cols-12 gap-8 lg:gap-10 items-stretch mb-16">
            <div class="lg:col-span-7">
              <blockquote class="relative h-full rounded-3xl shadow-soft-xl border border-black/5 overflow-hidden bg-white">
                <div
                  class="absolute inset-0 opacity-[0.06] pointer-events-none"
                  style="background-image: radial-gradient(circle at 10% 10%, #7EBEA6 0, transparent 40%), radial-gradient(circle at 90% 20%, #6B4B6E 0, transparent 45%);"
                ></div>

                <div class="relative p-10 sm:p-12">
                  <p class="text-brand-plum font-bold uppercase tracking-widest text-xs sm:text-sm mb-4">Our approach</p>
                  <p class="font-serif italic text-3xl sm:text-4xl text-brand-darker font-medium leading-snug">
                    “We exist to demystify regulation and support confident, competent construction.”
                  </p>

                  <div class="mt-8 flex flex-wrap gap-3">
                    <span class="px-4 py-2 rounded-full bg-brand-grey/70 ring-1 ring-black/5 text-brand-darker font-bold text-sm">CDM 2015</span>
                    <span class="px-4 py-2 rounded-full bg-brand-grey/70 ring-1 ring-black/5 text-brand-darker font-bold text-sm">Building Safety Act</span>
                    <span class="px-4 py-2 rounded-full bg-brand-grey/70 ring-1 ring-black/5 text-brand-darker font-bold text-sm">Welsh Gateway (emerging)</span>
                    <span class="px-4 py-2 rounded-full bg-brand-grey/70 ring-1 ring-black/5 text-brand-darker font-bold text-sm">Golden Thread</span>
                  </div>

                  <div class="mt-10 grid sm:grid-cols-3 gap-4">
                    <div class="rounded-2xl bg-brand-grey/60 ring-1 ring-black/5 p-4">
                      <div class="text-xs font-bold uppercase tracking-widest text-brand-dark/60">Focus</div>
                      <div class="mt-1 font-bold text-brand-darker">Risk + Dutyholders</div>
                    </div>
                    <div class="rounded-2xl bg-brand-grey/60 ring-1 ring-black/5 p-4">
                      <div class="text-xs font-bold uppercase tracking-widest text-brand-dark/60">Method</div>
                      <div class="mt-1 font-bold text-brand-darker">Structured evidence</div>
                    </div>
                    <div class="rounded-2xl bg-brand-grey/60 ring-1 ring-black/5 p-4">
                      <div class="text-xs font-bold uppercase tracking-widest text-brand-dark/60">Outcome</div>
                      <div class="mt-1 font-bold text-brand-darker">Defensible decisions</div>
                    </div>
                  </div>
                </div>
              </blockquote>
            </div>

            <div class="lg:col-span-5">
              <div class="h-full rounded-3xl bg-white border border-black/5 shadow-soft-xl p-10 sm:p-12">
                <p class="text-brand-green font-bold uppercase tracking-widest text-xs sm:text-sm mb-4">What you get</p>
                <h3 class="font-serif text-3xl font-black text-brand-darker mb-6 leading-tight">Clear advice. Defensible decisions. Better evidence.</h3>

                <ul class="space-y-4">
                  <li class="flex gap-4 items-start">
                    <span class="mt-1 w-10 h-10 rounded-2xl bg-brand-green/10 text-brand-green flex items-center justify-center shadow-sm">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5" aria-hidden="true"><path d="M5 13l4 4L19 7"></path></svg>
                    </span>
                    <div>
                      <p class="font-bold text-brand-darker">Proportionate compliance</p>
                      <p class="text-brand-dark/80">Practical support that matches risk, scope and dutyholder responsibilities.</p>
                    </div>
                  </li>

                  <li class="flex gap-4 items-start">
                    <span class="mt-1 w-10 h-10 rounded-2xl bg-brand-plum/10 text-brand-plum flex items-center justify-center shadow-sm">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5" aria-hidden="true"><path d="M12 3l9 5-9 5-9-5 9-5z"></path><path d="M3 12l9 5 9-5"></path><path d="M3 16l9 5 9-5"></path></svg>
                    </span>
                    <div>
                      <p class="font-bold text-brand-darker">Stronger documentation</p>
                      <p class="text-brand-dark/80">Structured evidence, consistent reporting, and robust record keeping.</p>
                    </div>
                  </li>

                  <li class="flex gap-4 items-start">
                    <span class="mt-1 w-10 h-10 rounded-2xl bg-black/5 text-brand-darker flex items-center justify-center shadow-sm">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5" aria-hidden="true"><path d="M8 12l2-2a2.8 2.8 0 014 0l2 2"></path><path d="M4 13l3-3 3 3-3 3-3-3z"></path><path d="M14 13l3-3 3 3-3 3-3-3z"></path></svg>
                    </span>
                    <div>
                      <p class="font-bold text-brand-darker">A team behind the work</p>
                      <p class="text-brand-dark/80">Technical delivery backed by professional ops and finance support.</p>
                    </div>
                  </li>
                </ul>

                <a
                  href="#contact"
                  class="mt-8 inline-flex w-full items-center justify-center px-8 py-4 rounded-xl bg-brand-darker text-white font-bold shadow-lg hover:bg-brand-darker/90 transition transform hover:-translate-y-0.5 focus:outline-none focus:ring-4 focus:ring-brand-green/30"
                >
                  Speak to the team <svg class="w-5 h-5 ml-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"></path></svg>
                </a>

                <p class="mt-4 text-xs text-brand-dark/60 font-semibold text-center">No spam. No pressure. Just clear next steps.</p>
              </div>
            </div>
          </div>

          <div class="text-center mb-12">
            <h3 class="text-brand-green font-bold uppercase tracking-widest mb-4 text-sm">Meet the Team</h3>
            <h2 class="font-serif text-4xl md:text-5xl font-black text-brand-darker">People you can rely on</h2>
            <p class="mt-5 text-lg sm:text-xl text-brand-dark max-w-4xl mx-auto leading-relaxed font-light">
              Principal Designers, technical advisors, and operations professionals supporting compliance across England and Wales.
            </p>
          </div>

          <div class="grid md:grid-cols-2 xl:grid-cols-3 gap-8 lg:gap-10 mb-10">
            <article
              v-for="leader in teamLeaders"
              :key="leader.key"
              class="group rounded-3xl bg-white border border-black/5 shadow-soft-xl overflow-hidden hover:-translate-y-1 transition-all duration-300"
            >
              <div class="relative">
                <div class="absolute inset-0 opacity-90" :class="leader.headerGradient"></div>
                <div class="relative p-8 sm:p-9">
                  <div class="flex items-start justify-between gap-6">
                    <div class="min-w-0">
                      <p class="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white/95 px-3 py-1 rounded-full shadow-sm ring-1 ring-white/15 bg-white/15 backdrop-blur-md">
                        <span v-html="iconSvg(leader.badgeIcon, 'w-4 h-4')"></span> {{ leader.badge }}
                      </p>

                      <h3 class="mt-5 font-serif text-3xl font-black text-white leading-tight">
                        {{ leader.name }}
                        <span class="block sm:inline sm:ml-2 text-white/85 font-bold text-base sm:text-lg">{{ leader.quals }}</span>
                      </h3>

                      <p class="mt-2 text-white/85 font-bold uppercase tracking-widest text-xs sm:text-sm">
                        {{ leader.subtitle }}
                      </p>
                    </div>

                    <div class="shrink-0">
                      <div class="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-white/15 border border-white/20 backdrop-blur-md flex items-center justify-center shadow-sm">
                        <span class="font-black text-white text-lg sm:text-xl tracking-tight">{{ initials(leader.name) }}</span>
                      </div>
                    </div>
                  </div>

                  <div class="mt-6 flex flex-wrap gap-2">
                    <span
                      v-for="chip in leader.chips"
                      :key="chip"
                      class="px-3 py-1 rounded-full bg-white/15 border border-white/20 text-white/90 text-xs font-bold uppercase tracking-widest"
                    >
                      {{ chip }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="p-9 sm:p-10">
                <p class="text-brand-dark text-lg leading-relaxed">
                  {{ leader.bio }}
                </p>

                <div class="mt-6 rounded-2xl bg-brand-grey p-5 ring-1 ring-black/5">
                  <p class="font-bold text-brand-darker uppercase tracking-widest text-xs mb-3">{{ leader.panelTitle }}</p>
                  <ul class="space-y-2 text-brand-dark font-medium">
                    <li v-for="(item, idx) in leader.panelItems" :key="idx" class="flex items-start gap-3">
                      <svg class="w-4 h-4 text-brand-green mt-1 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><path d="M5 13l4 4L19 7"></path></svg> {{ item }}
                    </li>
                  </ul>
                  <p v-if="leader.panelNote" class="mt-4 text-sm text-brand-dark/70 font-semibold">{{ leader.panelNote }}</p>
                </div>

                <div class="mt-6 grid sm:grid-cols-2 gap-3">
                  <a
                    :href="leader.emailHref"
                    class="inline-flex items-center justify-center px-5 py-3 rounded-xl bg-white ring-1 ring-black/5 font-bold text-brand-darker hover:bg-brand-grey transition focus:outline-none focus:ring-4 focus:ring-brand-green/20"
                  >
                    <svg class="w-5 h-5 mr-3" :class="leader.iconColor" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M4 6h16v12H4z"></path><path d="M4 8l8 6 8-6"></path></svg> Email
                  </a>
                  <a
                    :href="leader.phoneHref"
                    class="inline-flex items-center justify-center px-5 py-3 rounded-xl bg-white ring-1 ring-black/5 font-bold text-brand-darker hover:bg-brand-grey transition focus:outline-none focus:ring-4 focus:ring-brand-green/20"
                  >
                    <svg class="w-5 h-5 mr-3" :class="leader.iconColor" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M22 16.9v3a2 2 0 01-2.2 2 19.8 19.8 0 01-8.6-3.1 19.4 19.4 0 01-6-6A19.8 19.8 0 012.1 4.2 2 2 0 014.1 2h3a2 2 0 012 1.7l.4 2.6a2 2 0 01-.6 1.8L7.1 9.9a16 16 0 007 7l1.8-1.8a2 2 0 011.8-.6l2.6.4A2 2 0 0122 16.9z"></path></svg> Call
                  </a>
                </div>
              </div>
            </article>
          </div>

          <div class="grid md:grid-cols-3 gap-7 lg:gap-8">
            <article
              v-for="member in teamSupport"
              :key="member.key"
              class="rounded-3xl bg-white border border-black/5 shadow-sm hover:shadow-soft-xl transition-all duration-300 p-8 relative overflow-hidden"
            >
              <div class="absolute -top-12 -right-12 w-44 h-44 rounded-full opacity-10 blur-2xl" :class="member.blob"></div>

              <div class="relative">
                <div class="flex items-start justify-between gap-4">
                  <div class="min-w-0">
                    <p class="text-xs font-bold uppercase tracking-widest" :class="member.kickerColor">{{ member.kicker }}</p>
                    <h4 class="mt-2 font-serif text-2xl font-black text-brand-darker truncate">{{ member.name }}</h4>
                    <p class="mt-2 text-brand-dark/80 font-medium">{{ member.role }}</p>
                  </div>

                  <div class="w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm ring-1 ring-black/5" :class="member.iconWrap">
                    <span v-html="iconSvg(member.icon, 'w-5 h-5')"></span>
                  </div>
                </div>

                <p class="mt-5 text-brand-dark leading-relaxed">
                  {{ member.bio }}
                </p>

                <div v-if="member.footer" class="mt-6 rounded-2xl bg-brand-grey p-4 ring-1 ring-black/5 text-sm text-brand-dark/80 font-semibold">
                  {{ member.footer }}
                </div>

                <a
                  v-if="member.emailHref"
                  :href="member.emailHref"
                  class="mt-6 inline-flex w-full items-center justify-center px-5 py-3 rounded-xl bg-brand-darker text-white font-bold hover:bg-brand-darker/90 transition focus:outline-none focus:ring-4 focus:ring-brand-green/20"
                >
                  <svg class="w-5 h-5 mr-3 text-brand-green" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M4 6h16v12H4z"></path><path d="M4 8l8 6 8-6"></path></svg> Email {{ member.emailLabel }}
                </a>
              </div>
            </article>
          </div>

          <div class="mt-12 lg:mt-14 rounded-3xl bg-brand-darker text-white p-8 sm:p-10 shadow-2xl flex flex-col lg:flex-row items-center justify-between gap-6">
            <div class="text-center lg:text-left">
              <p class="text-white/70 font-bold uppercase tracking-widest text-xs sm:text-sm">Talk to us</p>
              <h3 class="mt-2 font-serif text-3xl sm:text-4xl font-black">Need help with CDM, Building Regs or evidence readiness?</h3>
              <p class="mt-3 text-white/80 text-lg">Book a 30-minute call and leave with clear next steps.</p>
            </div>

            <a
              href="#contact"
              class="inline-flex items-center justify-center px-10 py-4 rounded-xl bg-brand-green text-white font-bold shadow-lg hover:bg-[#7ab59c] transition transform hover:-translate-y-0.5 whitespace-nowrap focus:outline-none focus:ring-4 focus:ring-brand-green/30"
            >
              Book a Consultation <svg class="w-5 h-5 ml-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"></path></svg>
            </a>
          </div>
        </div>
      </section>

      <section id="contact" class="py-24 lg:py-28 bg-brand-darker relative overflow-hidden" aria-label="Contact">
        <div class="absolute top-0 right-0 -mr-48 -mt-48 w-[40rem] h-[40rem] bg-brand-green rounded-full opacity-10 blur-3xl pointer-events-none mix-blend-screen"></div>
        <div class="absolute bottom-0 left-0 -ml-48 -mb-48 w-[40rem] h-[40rem] bg-brand-plum rounded-full opacity-10 blur-3xl pointer-events-none mix-blend-screen"></div>

        <div class="relative z-10 max-w-5xl mx-auto px-4 text-center">
          <h2 class="font-serif text-4xl md:text-6xl font-black text-white mb-10 leading-tight tracking-tight">
            Need to speak with someone who actually understands what a Principal Designer does?
          </h2>

          <div class="bg-white/5 p-10 sm:p-12 rounded-3xl border border-white/10 backdrop-blur-md mb-12 shadow-2xl max-w-4xl mx-auto">
            <p class="text-2xl text-white mb-8 font-bold">Book a 30-minute call to discuss:</p>
            <div class="flex flex-col md:flex-row justify-center gap-4 md:gap-10 text-left md:text-center text-white text-xl font-semibold">
              <div class="flex items-center bg-white/10 px-6 py-3 rounded-full"><svg class="w-6 h-6 text-brand-green mr-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><path d="M5 13l4 4L19 7"></path></svg>Your upcoming project</div>
              <div class="flex items-center bg-white/10 px-6 py-3 rounded-full"><svg class="w-6 h-6 text-brand-green mr-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><path d="M5 13l4 4L19 7"></path></svg>Your role under CDM/Regs</div>
              <div class="flex items-center bg-white/10 px-6 py-3 rounded-full"><svg class="w-6 h-6 text-brand-green mr-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><path d="M5 13l4 4L19 7"></path></svg>Compliance risks</div>
            </div>
          </div>

          <a
            href="#"
            class="inline-block px-12 py-5 bg-brand-green text-white font-bold rounded-md shadow-xl hover:bg-[#7ab59c] transition transform hover:-translate-y-1 text-xl md:text-2xl tracking-wide uppercase"
            @click.prevent="openDiscovery"
          >
            📅 Book a Free Discovery Call
          </a>
        </div>
      </section>

      <section id="supplier-onboarding" class="py-24 lg:py-28 bg-white" aria-label="Supplier onboarding form">
        <div class="max-w-7xl xl:max-w-[70%] mx-auto px-4 sm:px-6 lg:px-8">
          <div class="text-center max-w-3xl mx-auto">
            <h3 class="text-brand-green font-bold uppercase tracking-widest mb-4 text-sm">Supplier Competence | Insurance | Due Diligence</h3>
            <h2 class="font-serif text-4xl md:text-5xl font-black text-brand-darker">Supplier Onboarding Form</h2>
            <p class="mt-5 text-lg text-brand-dark leading-relaxed">
              This form records the information and evidence required to assess and approve a supplier, consultant or specialist adviser for work undertaken by Deacon Marriner LLP. The supplier should complete Parts 1 to 7 and provide the requested evidence. Deacon Marriner LLP should complete Part 8 before any appointment or instruction to proceed.
            </p>

            <button
              type="button"
              class="mt-10 inline-block px-10 py-5 bg-brand-green text-white font-bold rounded-md shadow-xl hover:bg-[#7ab59c] transition transform hover:-translate-y-1 text-lg tracking-wide uppercase"
              @click="openSupplierForm"
            >
              Open Supplier Onboarding Form
            </button>
          </div>
        </div>
      </section>

      <section id="insights" class="py-24 lg:py-28 bg-brand-grey" aria-label="Insights">
        <div class="max-w-7xl xl:max-w-[70%] mx-auto px-4 sm:px-6 lg:px-8">
          <div class="flex flex-col md:flex-row justify-between items-end mb-14 pb-6 border-b-2 border-gray-200">
            <div>
              <h3 class="text-brand-green font-bold uppercase tracking-widest mb-4 text-sm">Knowledge Hub</h3>
              <h2 class="font-serif text-5xl font-bold text-brand-darker">Latest Insights</h2>
            </div>
          </div>

          <div v-if="sitePostsLoading" class="text-center py-10 text-brand-dark/70 font-semibold">Loading posts…</div>

          <div v-else class="grid md:grid-cols-3 gap-8 lg:gap-10">
            <article
              v-for="a in articles"
              :key="a.slug"
              class="group bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-soft-xl transition-all duration-500 hover:-translate-y-2 cursor-pointer"
              @click="openArticle(a)"
              role="button"
              tabindex="0"
              @keydown.enter.prevent="openArticle(a)"
              @keydown.space.prevent="openArticle(a)"
            >
              <div class="h-64 bg-gray-200 overflow-hidden relative">
                <img :src="a.image" :alt="a.imageAlt" class="w-full h-full object-cover group-hover:scale-105 transition duration-700" loading="lazy" />
                <div class="absolute top-5 left-5 text-white text-xs font-bold px-4 py-2 rounded-full uppercase tracking-widest shadow-sm" :class="a.badgeClass">{{ a.badge }}</div>
              </div>
              <div class="p-10">
                <div class="text-sm text-gray-400 mb-4 font-medium uppercase tracking-wider"><time :datetime="a.datetime">{{ a.dateLabel }}</time></div>
                <h3 class="font-serif font-bold text-2xl mb-4 text-brand-darker group-hover:text-brand-plum transition leading-tight">{{ a.title }}</h3>
                <p class="text-brand-dark line-clamp-3 mb-8 text-lg leading-relaxed">{{ a.excerpt }}</p>
                <a href="#" class="text-brand-plum font-bold text-base uppercase tracking-wide group-hover:underline" @click.prevent.stop="openArticle(a)">Read More</a>
              </div>
            </article>
          </div>

          <div v-if="!sitePostsLoading && articles.length === 0" class="text-center py-12 text-brand-dark/70 font-semibold">No posts yet.</div>
        </div>
      </section>

      <section id="projects" class="py-24 lg:py-28 bg-white" aria-label="Project video previews">
        <div class="max-w-7xl xl:max-w-[70%] mx-auto px-4 sm:px-6 lg:px-8">
          <div class="flex flex-col md:flex-row justify-between items-end mb-14 pb-6 border-b-2 border-gray-200">
            <div>
              <h3 class="text-brand-green font-bold uppercase tracking-widest mb-4 text-sm">Project Videos</h3>
              <h2 class="font-serif text-5xl font-bold text-brand-darker">Latest Project Updates</h2>
            </div>
          </div>

          <div v-if="projectVideosLoading" class="text-center py-10 text-brand-dark/70 font-semibold">Loading videos…</div>

          <div v-else class="grid md:grid-cols-3 gap-8 lg:gap-10">
            <article
              v-for="v in projectVideos"
              :key="v.id"
              class="group bg-brand-grey rounded-3xl overflow-hidden shadow-sm hover:shadow-soft-xl transition-all duration-500 hover:-translate-y-2 cursor-pointer"
              @click="openVideo(v)"
              role="button"
              tabindex="0"
              @keydown.enter.prevent="openVideo(v)"
              @keydown.space.prevent="openVideo(v)"
            >
              <div class="relative aspect-video bg-brand-darker overflow-hidden">
                <video
                  :ref="setVideoRef(v.id)"
                  :src="v.videoUrl"
                  class="absolute inset-0 w-full h-full object-cover"
                  preload="metadata"
                  muted
                  loop
                  playsinline
                  @mouseenter="previewPlay(v.id)"
                  @mouseleave="previewPause(v.id)"
                >
                  Sorry, your browser does not support embedded videos.
                </video>
                <div class="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                <div class="absolute top-5 left-5 text-white text-xs font-bold px-4 py-2 rounded-full uppercase tracking-widest shadow-sm" :class="v.statusClass">{{ v.status }}</div>
                <div class="absolute inset-0 flex items-center justify-center text-white">
                  <span class="w-14 h-14 rounded-full bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center shadow-lg group-hover:scale-110 transition">
                    <svg class="w-6 h-6 ml-1" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7-11-7z"></path></svg>
                  </span>
                </div>
              </div>
              <div class="p-8">
                <div class="flex flex-wrap items-center gap-3 mb-4 text-xs font-bold uppercase tracking-widest text-brand-dark/50">
                  <span>{{ v.location }}</span><span>•</span><span>{{ v.phase }}</span>
                </div>
                <h3 class="font-serif font-bold text-2xl mb-4 text-brand-darker group-hover:text-brand-plum transition leading-tight">{{ v.title }}</h3>
                <p class="text-brand-dark line-clamp-3 mb-8 text-lg leading-relaxed">{{ v.description }}</p>
                <a href="#" class="text-brand-plum font-bold text-base uppercase tracking-wide group-hover:underline" @click.prevent.stop="openVideo(v)">Read More</a>
              </div>
            </article>
          </div>

          <div v-if="!projectVideosLoading && projectVideos.length === 0" class="text-center py-12 text-brand-dark/70 font-semibold">No project videos yet.</div>
        </div>
      </section>
    </main>

    <footer class="bg-brand-darker text-gray-300 py-20 lg:py-24" aria-label="Footer">
      <div class="xl:max-w-[70%] mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid md:grid-cols-5 gap-12 lg:gap-16 mb-14">
          <div class="col-span-2">
            <img src="/logo.webp" alt="Deacon Marriner LLP" class="h-16 md:h-20 w-auto mb-8 brightness-0 invert opacity-90" loading="lazy" />
            <p class="mb-10 max-w-md text-gray-400 leading-relaxed text-lg">
              Helping construction professionals navigate building regulations, CDM 2015, and the evolving Welsh safety regime with confidence and clarity.
            </p>
            <div class="flex space-x-4">
              <a href="https://www.facebook.com/deaconmarriner/?fref=ts" class="text-gray-400 hover:text-brand-green transition hover:scale-110 transform bg-white/5 p-3 rounded-full" aria-label="Facebook">
                <svg class="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M13 22v-8h3l.5-4H13V7.7c0-1.2.4-2 2.1-2H17V2.2c-.3 0-1.4-.2-2.8-.2-2.8 0-4.7 1.7-4.7 4.9V10H6v4h3.5v8H13z"></path></svg>
              </a>
            </div>
          </div>

          <div>
            <h4 class="text-white font-bold mb-6 uppercase tracking-widest text-sm">Company</h4>
            <ul class="space-y-3 text-base font-medium">
              <li><a href="#about" class="hover:text-brand-green transition inline-block hover:translate-x-1">About Us</a></li>
              <li><a href="#services" class="hover:text-brand-green transition inline-block hover:translate-x-1">Services</a></li>
              <li><a href="#insights" class="hover:text-brand-green transition inline-block hover:translate-x-1">Insights</a></li>
              <li><a href="#contact" class="hover:text-brand-green transition inline-block hover:translate-x-1">Contact</a></li>
            </ul>
          </div>

          <div class="col-span-2">
            <h4 class="text-white font-bold mb-6 uppercase tracking-widest text-sm">Stay Informed</h4>
            <p class="text-base mb-6 text-gray-400 leading-relaxed">
              Get the latest critical updates on CDM, building safety, and Welsh law directly to your inbox. No spam, just compliance.
            </p>
          </div>
        </div>

        <div class="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-500 font-medium">
          <p>&copy; 2016-{{ new Date().getFullYear() }} Deacon Marriner LLP. All rights reserved.</p>
          <div class="space-x-6 mt-5 md:mt-0">
            <a href="https://admin.deaconmarriner.co.uk" target="_blank" class="hover:text-brand-green transition cursor-pointer">Blog Admin</a>
          </div>
        </div>
      </div>
    </footer>

    <div v-if="openSupplierFormModal" class="fixed inset-0 z-[60] bg-white flex flex-col" role="dialog" aria-modal="true" aria-labelledby="supplier-form-title">
      <div class="flex items-center justify-between gap-4 px-4 sm:px-8 py-4 border-b border-black/10 shrink-0">
        <div>
          <h3 id="supplier-form-title" class="font-serif text-xl sm:text-2xl font-black text-brand-darker">Supplier Onboarding Form</h3>
          <p class="mt-0.5 text-sm text-brand-dark hidden sm:block">Please complete all sections before submitting.</p>
        </div>

        <button class="shrink-0 w-10 h-10 rounded-full bg-brand-grey hover:bg-black/5 text-brand-darker flex items-center justify-center transition" type="button" @click="closeSupplierForm" aria-label="Close">
          <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"></path></svg>
        </button>
      </div>

      <iframe
        src="https://form.jotform.com/262585098135061"
        title="Deacon Marriner LLP supplier onboarding form"
        class="w-full flex-1"
        style="border: 0;"
        loading="lazy"
      ></iframe>
    </div>

    <div v-if="openDiscoveryModal" class="fixed inset-0 z-[60]">
      <div class="absolute inset-0 bg-black/60" @click="closeDiscovery"></div>

      <div class="relative mx-auto mt-24 w-[92%] max-w-xl rounded-3xl bg-white p-7 sm:p-8 shadow-2xl ring-1 ring-black/10" role="dialog" aria-modal="true">
        <div class="flex items-start justify-between gap-4">
          <div>
            <h3 class="font-serif text-3xl font-black text-brand-darker">Book a Free Discovery Call</h3>
            <p class="mt-2 text-brand-dark text-lg">Share a few details and we’ll contact you to arrange a suitable time.</p>
          </div>

          <button class="text-brand-darker/70 hover:text-brand-darker" type="button" @click="closeDiscovery" aria-label="Close">
            <svg class="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"></path></svg>
          </button>
        </div>

        <form class="mt-6 space-y-4" @submit.prevent="submitDiscovery">
          <div>
            <label class="block text-sm font-semibold text-brand-darker mb-1">Full name *</label>
            <input v-model.trim="dcForm.name" type="text" required autocomplete="name" class="w-full rounded-xl bg-brand-grey px-4 py-3 text-brand-darker outline-none ring-1 ring-black/10 focus:ring-2 focus:ring-brand-green" />
          </div>

          <div>
            <label class="block text-sm font-semibold text-brand-darker mb-1">Email address *</label>
            <input v-model.trim="dcForm.email" type="email" required autocomplete="email" class="w-full rounded-xl bg-brand-grey px-4 py-3 text-brand-darker outline-none ring-1 ring-black/10 focus:ring-2 focus:ring-brand-green" />
          </div>

          <div>
            <label class="block text-sm font-semibold text-brand-darker mb-1">Contact number *</label>
            <input v-model.trim="dcForm.phone" type="tel" required autocomplete="tel" class="w-full rounded-xl bg-brand-grey px-4 py-3 text-brand-darker outline-none ring-1 ring-black/10 focus:ring-2 focus:ring-brand-green" />
          </div>

          <div>
            <label class="block text-sm font-semibold text-brand-darker mb-1">Anything else that may be relevant</label>
            <textarea v-model.trim="dcForm.notes" rows="4" class="w-full resize-none rounded-xl bg-brand-grey px-4 py-3 text-brand-darker outline-none ring-1 ring-black/10 focus:ring-2 focus:ring-brand-green" placeholder="e.g. project type, location, timescales, your role, key concerns…"></textarea>
          </div>

          <div v-if="dcStatus.type === 'error'" class="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700 ring-1 ring-red-100">{{ dcStatus.message }}</div>
          <div v-if="dcStatus.type === 'success'" class="rounded-xl bg-green-50 px-4 py-3 text-sm text-green-800 ring-1 ring-green-100">{{ dcStatus.message }}</div>

          <div class="flex items-center justify-end gap-3 pt-2">
            <button type="button" class="px-5 py-3 rounded-xl font-bold text-brand-darker hover:bg-brand-grey transition" @click="closeDiscovery" :disabled="dcLoading">Cancel</button>
            <button type="submit" class="px-6 py-3 rounded-xl font-bold text-white bg-brand-green hover:bg-[#7ab59c] transition disabled:opacity-60" :disabled="dcLoading">
              {{ dcLoading ? "Sending…" : "Send request" }}
            </button>
          </div>

          <p class="text-xs text-brand-darker/60 pt-2">No spam. We’ll only use this information to respond to your request.</p>
        </form>
      </div>
    </div>

    <div v-if="openArticleModal && activeArticle" class="fixed inset-0 z-[70]">
      <div class="absolute inset-0 bg-black/70" @click="closeArticle"></div>

      <div class="relative mx-auto mt-10 sm:mt-14 w-[94%] max-w-4xl rounded-3xl bg-white shadow-2xl ring-1 ring-black/10 overflow-hidden">
        <div class="relative h-56 sm:h-72 bg-gray-200">
          <img :src="activeArticle.image || activeArticle.imageUrl" :alt="activeArticle.imageAlt" class="absolute inset-0 w-full h-full object-cover" />
          <div class="absolute inset-0 bg-gradient-to-t from-black/65 via-black/25 to-black/10"></div>

          <button class="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-md border border-white/20 text-white flex items-center justify-center transition" type="button" @click="closeArticle" aria-label="Close article">
            <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"></path></svg>
          </button>

          <div class="absolute bottom-5 left-5 right-5 sm:bottom-7 sm:left-7 sm:right-7">
            <div class="flex flex-wrap items-center gap-3 mb-3">
              <span class="text-xs font-bold px-3 py-1 rounded-full uppercase tracking-widest text-white shadow-sm" :class="activeArticle.badgeClass">{{ activeArticle.badge }}</span>
              <span class="text-xs sm:text-sm text-white/80 font-semibold uppercase tracking-wider"><time :datetime="activeArticle.datetime">{{ activeArticle.dateLabel }}</time></span>
              <span class="text-xs sm:text-sm text-white/60 font-semibold uppercase tracking-wider">• {{ activeArticle.readTime }}</span>
            </div>

            <h3 class="font-serif text-2xl sm:text-4xl font-black text-white leading-tight drop-shadow">{{ activeArticle.title }}</h3>

            <p class="mt-3 text-white/90 text-sm sm:text-base leading-relaxed max-w-3xl">{{ activeArticle.excerpt }}</p>
          </div>
        </div>

        <div class="px-6 sm:px-10 py-8 sm:py-10 max-h-[70vh] overflow-auto">
          <div class="mx-auto max-w-2xl">
            <div class="article-prose" v-html="activeArticle.contentHtml"></div>

            <div class="my-10 h-px bg-black/10"></div>

            <div class="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
              <button
                class="w-full sm:w-auto px-5 py-3 rounded-xl font-bold text-brand-darker bg-brand-grey hover:bg-black/5 transition disabled:opacity-50 disabled:hover:bg-brand-grey"
                type="button"
                :disabled="!hasPrevArticle"
                @click="openPrevArticle"
              >
                ← Previous
              </button>

              <button class="w-full sm:w-auto px-6 py-3 rounded-xl font-bold text-white bg-brand-plum hover:bg-brand-plum/90 transition" type="button" @click="closeArticle">
                Close
              </button>

              <button
                class="w-full sm:w-auto px-5 py-3 rounded-xl font-bold text-brand-darker bg-brand-grey hover:bg-black/5 transition disabled:opacity-50 disabled:hover:bg-brand-grey"
                type="button"
                :disabled="!hasNextArticle"
                @click="openNextArticle"
              >
                Next →
              </button>
            </div>
            <p class="mt-6 text-xs text-gray-500 text-center">Tip: Press <span class="font-bold">Esc</span> to close this article.</p>
          </div>
        </div>
      </div>
    </div>

    <div v-if="openVideoModal && activeVideo" class="fixed inset-0 z-[72] flex items-center justify-center" style="padding: 2rem;">
      <div class="absolute inset-0 bg-black/70" @click="closeVideo"></div>

      <div
        class="relative w-full rounded-3xl bg-white shadow-2xl ring-1 ring-black/10 overflow-hidden flex flex-col lg:flex-row"
        role="dialog"
        aria-modal="true"
        aria-labelledby="project-video-title"
        style="max-width: 82rem; max-height: calc(-4rem + 100vh);"
      >
        <div class="lg:w-[55%] flex flex-col" style="overflow-y: auto;">
          <div class="sticky top-0 z-10 bg-white border-b border-black/8 px-6 sm:px-8 py-4 flex items-center justify-between gap-4 shrink-0">
            <div class="flex flex-wrap items-center gap-2">
              <span class="text-xs font-bold px-3 py-1 rounded-full uppercase tracking-widest text-white" :class="activeVideo.statusClass">{{ activeVideo.status }}</span>
              <span class="text-xs font-bold uppercase tracking-widest text-brand-dark/40">{{ activeVideo.duration }}</span>
            </div>
            <button class="shrink-0 w-9 h-9 rounded-full bg-brand-grey hover:bg-black/5 text-brand-darker flex items-center justify-center transition" type="button" @click="closeVideo" aria-label="Close project video details">
              <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"></path></svg>
            </button>
          </div>

          <div class="px-6 sm:px-8 py-6 flex flex-col gap-5">
            <h3 id="project-video-title" class="font-serif text-2xl sm:text-3xl font-black text-brand-darker leading-tight">{{ activeVideo.title }}</h3>

            <div class="grid grid-cols-2 gap-3">
              <div class="rounded-2xl bg-brand-grey p-4">
                <div class="text-xs font-bold uppercase tracking-widest text-brand-dark/50 mb-1">Location</div>
                <div class="font-bold text-brand-darker text-sm">{{ activeVideo.location }}</div>
              </div>
              <div class="rounded-2xl bg-brand-grey p-4">
                <div class="text-xs font-bold uppercase tracking-widest text-brand-dark/50 mb-1">Phase</div>
                <div class="font-bold text-brand-darker text-sm">{{ activeVideo.phase }}</div>
              </div>
            </div>

            <p class="text-base leading-relaxed text-brand-dark" style="white-space: pre-line;">{{ activeVideo.description }}</p>

            <div class="pt-4 border-t border-black/10 flex justify-end mt-auto">
              <button type="button" class="px-6 py-3 rounded-xl font-bold text-white bg-brand-plum hover:bg-brand-plum/90 transition" @click="closeVideo">Close</button>
            </div>
          </div>
        </div>

        <div class="bg-brand-darker lg:w-[45%] flex-shrink-0 flex items-center justify-center p-5 lg:p-8">
          <div class="relative w-full" style="aspect-ratio: 16 / 9;">
            <video :src="activeVideo.videoUrl" class="absolute inset-0 w-full h-full object-cover rounded-2xl" controls playsinline preload="metadata">
              Sorry, your browser does not support embedded videos.
            </video>
          </div>
        </div>
      </div>
    </div>

    <a
      @click="scrollToTop()"
      class="fixed bottom-5 right-5 z-40 w-12 h-12 rounded-full bg-white/95 border border-black/10 shadow-soft-xl flex items-center justify-center text-brand-darker hover:bg-white transition transform hover:-translate-y-0.5"
      aria-label="Back to top"
    >
      <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" aria-hidden="true"><path d="M12 19V5M5 12l7-7 7 7"></path></svg>
    </a>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue";

const POSTS_URL = "https://deaconmarriner-backend.contact-cde.workers.dev/posts";
const TEAM_URL = "https://deaconmarriner-backend.contact-cde.workers.dev/team";
const PROJECT_VIDEOS_URL = "https://deaconmarriner-backend.contact-cde.workers.dev/project-videos";

const menuOpen = ref(false);

function toggleMenu() {
  menuOpen.value = !menuOpen.value;
}

function closeMenu() {
  menuOpen.value = false;
}

function scrollToTop() {
  window.scrollTo({ top: 0, behavior: "smooth" });
}

const ICON_DEFS = {
  "shield-halved": { fill: true, paths: ["M12 2l7 3v5c0 5-3.4 9.4-7 10-3.6-.6-7-5-7-10V5l7-3z"] },
  gear: {
    fill: true,
    paths: [
      "M12 8.5A3.5 3.5 0 1112 15.5 3.5 3.5 0 0112 8.5zm8 3.5l-1.8-.6a6.9 6.9 0 00-.5-1.2l.8-1.7-1.7-1.7-1.7.8a6.9 6.9 0 00-1.2-.5L12 4l-1 .1-1 .1-.6 1.8a6.9 6.9 0 00-1.2.5l-1.7-.8-1.7 1.7.8 1.7a6.9 6.9 0 00-.5 1.2L4 12l.1 1 .1 1 .8.3.9.3c.1.4.3.8.5 1.2l-.8 1.7 1.7 1.7 1.7-.8c.4.2.8.4 1.2.5l.6 1.8 1 .1 1-.1.3-.9.3-.9c.4-.1.8-.3 1.2-.5l1.7.8 1.7-1.7-.8-1.7c.2-.4.4-.8.5-1.2l1.8-.6.1-1-.1-1z",
    ],
  },
  "clipboard-list": { paths: ["M9 3h6", "M9 3a2 2 0 00-2 2v1h10V5a2 2 0 00-2-2", "M7 6H6a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V8a2 2 0 00-2-2h-1"] },
  "diagram-project": { paths: ["M8 8h8", "M8 16h8", "M4 8h.01", "M4 16h.01", "M12 8v8"] },
  receipt: { fill: true, paths: ["M6 2h12a1 1 0 011 1v18l-2.5-1.5L14 21l-2-1.5L10 21l-2.5-1.5L5 21V3a1 1 0 011-1z", "M8 7h8", "M8 11h8", "M8 15h5"] },
};

function iconSvg(faClass, sizeClass) {
  const slug = String(faClass || "")
    .trim()
    .split(/\s+/)
    .pop()
    ?.replace(/^fa-/, "");
  const def = (slug && ICON_DEFS[slug]) || ICON_DEFS["clipboard-list"];
  if (!def) return "";
  const fillAttr = def.fill ? 'fill="currentColor" stroke="none"' : 'fill="none" stroke="currentColor"';
  const strokeWidth = def.strokeWidth || 2;
  const linecap = def.fill ? "" : 'stroke-linecap="round" stroke-linejoin="round"';
  const paths = def.paths.map((d) => `<path d="${d}"></path>`).join("");
  return `<svg viewBox="0 0 24 24" ${fillAttr} stroke-width="${strokeWidth}" ${linecap} class="${sizeClass}" aria-hidden="true">${paths}</svg>`;
}

function escapeHtml(s) {
  return String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function paragraphsToHtml(paras) {
  const safe = Array.isArray(paras) ? paras.filter(Boolean) : [];
  if (!safe.length) return "";
  return safe.map((p) => `<p>${escapeHtml(p)}</p>`).join("");
}

function normalizePost(p) {
  const fallback = paragraphsToHtml(Array.isArray(p?.content) ? p.content.filter(Boolean) : []);
  const html = String(p?.contentHtml || "").trim();

  return {
    slug: String(p?.slug || "").trim(),
    badge: p?.badge || "Insight",
    badgeClass: p?.badgeClass || "bg-brand-plum",
    datetime: p?.datetime || "",
    dateLabel: p?.dateLabel || "",
    readTime: p?.readTime || "5 min read",
    title: p?.title || "",
    excerpt: p?.excerpt || "",
    image: p?.imageUrl || p?.image || "",          
    imageAlt: p?.imageAlt || p?.title || "Article image",
    contentHtml: html || fallback || "<p></p>",
    imageUrl: p?.imageUrl || "",      
  };
}

function sortNewestFirst(list) {
  return [...list].sort((a, b) => {
    const ad = Date.parse(a.datetime || "") || 0;
    const bd = Date.parse(b.datetime || "") || 0;
    return bd - ad;
  });
}

const sitePostsLoading = ref(false);
const articles = ref([]);

async function loadPublicPosts() {
  sitePostsLoading.value = true;
  try {
    const res = await fetch(POSTS_URL, { cache: "no-store" });
    if (!res.ok) throw new Error("Failed to load posts");
    const data = await res.json();
    const posts = Array.isArray(data?.posts) ? data.posts.map(normalizePost) : [];
    articles.value = sortNewestFirst(posts.filter((p) => p.slug && p.title)).slice(0, 3);
  } catch {
    articles.value = [];
  } finally {
    sitePostsLoading.value = false;
  }
}

function normalizeProjectVideo(v) {
  return {
    id: String(v?.id || v?.slug || "").trim(),
    title: v?.title || "",
    location: v?.location || "",
    phase: v?.phase || "",
    status: v?.status || "Current",
    statusClass: v?.statusClass || "bg-brand-green",
    duration: v?.duration || "00:00",
    description: v?.description || "",
    videoUrl: v?.videoUrl || "",
    datetime: v?.datetime || "",
  };
}

const projectVideosLoading = ref(false);
const projectVideos = ref([]);

async function loadProjectVideos() {
  projectVideosLoading.value = true;
  try {
    const res = await fetch(PROJECT_VIDEOS_URL, { cache: "no-store" });
    if (!res.ok) throw new Error("Failed to load project videos");
    const data = await res.json();
    const videos = Array.isArray(data?.projectVideos) ? data.projectVideos.map(normalizeProjectVideo) : [];
    projectVideos.value = sortNewestFirst(videos.filter((v) => v.id && v.title));
  } catch {
    projectVideos.value = [];
  } finally {
    projectVideosLoading.value = false;
  }
}

const videoRefs = new Map();

function setVideoRef(id) {
  return (el) => {
    if (el) videoRefs.set(id, el);
    else videoRefs.delete(id);
  };
}

function previewPlay(id) {
  const el = videoRefs.get(id);
  if (!el) return;
  videoRefs.forEach((other, key) => {
    if (key !== id && !other.paused) other.pause();
  });
  el.play().catch(() => {});
}

function previewPause(id) {
  const el = videoRefs.get(id);
  if (el && !el.paused) el.pause();
}

const openVideoModal = ref(false);
const activeVideo = ref(null);

function openVideo(video) {
  activeVideo.value = video;
  openVideoModal.value = true;
}

function closeVideo() {
  openVideoModal.value = false;
  activeVideo.value = null;
}

const openArticleModal = ref(false);
const activeArticle = ref(null);

function openArticle(article) {
  activeArticle.value = article;
  openArticleModal.value = true;
}

function closeArticle() {
  openArticleModal.value = false;
  activeArticle.value = null;
}

const activeIndex = computed(() => {
  if (!activeArticle.value) return -1;
  return articles.value.findIndex((a) => a.slug === activeArticle.value.slug);
});

const hasPrevArticle = computed(() => activeIndex.value > 0);
const hasNextArticle = computed(() => activeIndex.value >= 0 && activeIndex.value < articles.value.length - 1);

function openArticleIndex(idx) {
  const a = articles.value[idx];
  if (a) openArticle(a);
}

function openPrevArticle() {
  if (hasPrevArticle.value) openArticleIndex(activeIndex.value - 1);
}

function openNextArticle() {
  if (hasNextArticle.value) openArticleIndex(activeIndex.value + 1);
}

const openSupplierFormModal = ref(false);

function openSupplierForm() {
  openSupplierFormModal.value = true;
}

function closeSupplierForm() {
  openSupplierFormModal.value = false;
}

const openDiscoveryModal = ref(false);
const dcLoading = ref(false);
const dcStatus = reactive({ type: "idle", message: "" });
const dcForm = reactive({ name: "", email: "", phone: "", notes: "" });

function openDiscovery() {
  openDiscoveryModal.value = true;
  dcStatus.type = "idle";
  dcStatus.message = "";
}

function closeDiscovery() {
  openDiscoveryModal.value = false;
  dcStatus.type = "idle";
  dcStatus.message = "";
}

function resetDiscoveryForm() {
  dcForm.name = "";
  dcForm.email = "";
  dcForm.phone = "";
  dcForm.notes = "";
}

async function submitDiscovery() {
  dcStatus.type = "idle";
  dcStatus.message = "";

  if (!dcForm.name || !dcForm.email || !dcForm.phone) {
    dcStatus.type = "error";
    dcStatus.message = "Please fill in your name, email, and contact number.";
    return;
  }

  dcLoading.value = true;

  try {
    const endpoint = "https://formcarry.com/s/eHPWqS2-7mg";
    const fd = new FormData();
    fd.append("name", dcForm.name);
    fd.append("email", dcForm.email);
    fd.append("phone", dcForm.phone);
    fd.append("notes", dcForm.notes);
    fd.append("pageUrl", window.location.href);
    fd.append("submittedAt", new Date().toISOString());

    const res = await fetch(endpoint, { method: "POST", body: fd, headers: { Accept: "application/json" } });

    if (!res.ok) {
      const data = await res.json().catch(() => null);
      throw new Error(data?.message || data?.error || "Failed to send. Please try again.");
    }

    dcStatus.type = "success";
    dcStatus.message = "Thanks — we’ve received your request and will be in touch shortly.";

    setTimeout(() => {
      closeDiscovery();
      resetDiscoveryForm();
    }, 900);
  } catch (err) {
    dcStatus.type = "error";
    dcStatus.message = err?.message || "Something went wrong. Please try again.";
  } finally {
    dcLoading.value = false;
  }
}

function initials(fullName) {
  const parts = String(fullName || "")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean);
  if (parts.length === 0) return "";
  const first = parts[0]?.[0] || "";
  const last = (parts.length > 1 ? parts[parts.length - 1]?.[0] : parts[0]?.[1]) || "";
  if ((first + last).toUpperCase() === "EC") return "JC";
  return (first + last).toUpperCase();
}

const defaultTeamLeaders = [
  {
    key: "katrina",
    badge: "Principal Designer",
    badgeIcon: "fas fa-shield-halved",
    headerGradient: "bg-gradient-to-br from-brand-green via-[#6FBFA0] to-brand-plum",
    iconColor: "text-brand-green",
    name: "Katrina Deacon",
    quals: "MAPM IMaPS NEBOSH",
    subtitle: "Director | Principal Designer | CDM & Safety Specialist",
    chips: ["CDM 2015", "IMaPS", "NEBOSH", "Healthcare"],
    bio:
      "Founding Director with 20+ years in the built environment. Accredited Principal Designer (IMaPS), NEBOSH-qualified, and MAPM—supporting clients with clear, proportionate and defensible compliance.",
    panelTitle: "Recent portfolio includes",
    panelItems: [
      "Royal Free London NHS Foundation Trust",
      "West Herts Hospitals NHS Trust (CDC, Endoscopy, Imaging, HV)",
      "Bedfordshire Hospitals NHS Trust",
    ],
    panelNote: "Works spanning RIBA Stages 1–6.",
    emailHref: "mailto:katrina@deaconmarriner.co.uk",
    phoneHref: "tel:+447974772597",
  },
  {
    key: "john",
    badge: "Technical Safety Lead",
    badgeIcon: "fas fa-gear",
    headerGradient: "bg-gradient-to-br from-brand-plum via-[#6B4B6E] to-[#2b2b2b]",
    iconColor: "text-brand-plum",
    name: "Eur Ing John Creaser",
    quals: "CEng BEng (Hons) FIHEEM MCIBSE",
    subtitle: "Chartered Engineer | PD (Building Regs) | Design Compliance",
    chips: ["Building Regs", "Healthcare", "Commissioning", "Design Assurance"],
    bio:
      "Chartered Mechanical Engineer with extensive healthcare and public sector experience—bringing technical depth to design compliance, Building Regulations assurance, and the emerging PD (Building Regulations) role.",
    panelTitle: "Experience includes",
    panelItems: [
      "Operating theatres, CT/MRI suites, cleanrooms",
      "Critical infrastructure phasing & commissioning",
      "Surveying & feasibility for remediation/change of use",
    ],
    panelNote: "",
    emailHref: "mailto:john@deaconmarriner.co.uk",
    phoneHref: "tel:+447811347843",
  },
];

const defaultTeamSupport = [
  {
    key: "dominic",
    kicker: "PD Assistant",
    kickerColor: "text-blue-700",
    name: "Dominic Cranney",
    role: "Safety & Compliance Support | Estates Liaison",
    iconWrap: "bg-blue-50 text-blue-600",
    icon: "fas fa-clipboard-list",
    blob: "bg-blue-500",
    bio:
      "Former Critical Care professional (13+ years) supporting PCI collation, H&S Files, and documentation monitoring. SMSTS-qualified; commencing formal CDM PD training in 2025.",
    footer: "",
    emailHref: "mailto:dominic@deaconmarriner.co.uk",
    emailLabel: "Dominic",
  },
  {
    key: "kyra",
    kicker: "Business Support",
    kickerColor: "text-yellow-700",
    name: "Kyra Cancel",
    role: "Finance & Business Support Lead",
    iconWrap: "bg-yellow-50 text-yellow-700",
    icon: "fas fa-receipt",
    blob: "bg-yellow-500",
    bio:
      "Oversees invoicing accuracy, time tracking, supplier onboarding, and purchase order tracking—ensuring a smooth, responsive client experience.",
    footer: "Clear billing • Contract support • Cost control",
    emailHref: "mailto:kyra@deaconmarriner.co.uk",
    emailLabel: "Kyra",
  },
  {
    key: "jean",
    kicker: "Operations",
    kickerColor: "text-indigo-700",
    name: "Jean Wright",
    role: "Operations & Administrative Coordinator",
    iconWrap: "bg-indigo-50 text-indigo-700",
    icon: "fas fa-diagram-project",
    blob: "bg-indigo-500",
    bio:
      "Coordinates documentation workflows, diary support, and training logistics—maintaining quality standards across templates, reporting formats and document control.",
    footer: "Document control • Training logistics • Quality standards",
    emailHref: "mailto:jean@deaconmarriner.co.uk",
    emailLabel: "Jean",
  },
];

const teamLeaders = ref([...defaultTeamLeaders]);
const teamSupport = ref([...defaultTeamSupport]);

const TEAM_CACHE_KEY = "dm_team_cache_v1";
const TEAM_CACHE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

function sortedByKey(list) {
  return [...(Array.isArray(list) ? list : [])].sort((a, b) => String(a?.key || "").localeCompare(String(b?.key || "")));
}

function stableStringify(obj) {
  const seen = new WeakSet();
  const sorter = (value) => {
    if (value && typeof value === "object") {
      if (seen.has(value)) return value;
      seen.add(value);
      if (Array.isArray(value)) return value.map(sorter);
      return Object.keys(value)
        .sort()
        .reduce((acc, k) => {
          acc[k] = sorter(value[k]);
          return acc;
        }, {});
    }
    return value;
  };
  return JSON.stringify(sorter(obj));
}

function teamEquals(a, b) {
  return stableStringify(a) === stableStringify(b);
}

function normalizeTeamPayload(data) {
  if (!data) return null;

  if (Array.isArray(data?.leaders) || Array.isArray(data?.support)) {
    const leaders = (Array.isArray(data.leaders) ? data.leaders : []).filter((m) => m?.key);
    const support = sortedByKey(data.support).filter((m) => m?.key);
    if (!leaders.length && !support.length) return null;
    return { leaders, support };
  }

  if (Array.isArray(data?.team)) {
    const leaders = data.team.filter((m) => m?.type === "leader" && m?.key);
    const support = sortedByKey(data.team.filter((m) => m?.type === "support")).filter((m) => m?.key);
    if (!leaders.length && !support.length) return null;
    return { leaders, support };
  }

  return null;
}

function applyTeamIfDifferent(incoming) {
  const defaults = { leaders: sortedByKey(defaultTeamLeaders), support: sortedByKey(defaultTeamSupport) };
  const comparableIncoming = { leaders: sortedByKey(incoming.leaders), support: sortedByKey(incoming.support) };

  if (teamEquals(comparableIncoming, defaults)) return false;

  teamLeaders.value = incoming.leaders;
  teamSupport.value = incoming.support;
  return true;
}

function readTeamCache() {
  try {
    const raw = localStorage.getItem(TEAM_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const ts = Number(parsed?.ts || 0);
    if (!ts || Date.now() - ts > TEAM_CACHE_MAX_AGE_MS) return null;
    const normalized = normalizeTeamPayload(parsed?.data);
    if (!normalized) return null;
    return normalized;
  } catch {
    return null;
  }
}

function writeTeamCache(team) {
  try {
    localStorage.setItem(TEAM_CACHE_KEY, JSON.stringify({ ts: Date.now(), data: team }));
  } catch {
    // ignore
  }
}

async function loadTeamWithCache() {
  const cached = readTeamCache();
  if (cached) applyTeamIfDifferent(cached);

  try {
    const res = await fetch(TEAM_URL, { cache: "no-store" });
    if (!res.ok) return;

    const data = await res.json().catch(() => null);
    const normalized = normalizeTeamPayload(data);
    if (!normalized) return;

    const applied = applyTeamIfDifferent(normalized);
    if (applied) writeTeamCache(normalized);
  } catch {
    // ignore
  }
}

function onKeydown(e) {
  if (e.key !== "Escape") return;
  closeMenu();
  closeSupplierForm();
  closeDiscovery();
  closeArticle();
  closeVideo();
}

const anyModalOpen = computed(
  () => openSupplierFormModal.value || openDiscoveryModal.value || openArticleModal.value || openVideoModal.value
);

watch(anyModalOpen, (v) => {
  document.documentElement.style.overflow = v ? "hidden" : "";
});

onMounted(async () => {
  document.addEventListener("keydown", onKeydown);
  await loadPublicPosts();
  await loadProjectVideos();
  await loadTeamWithCache();
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", onKeydown);
  document.documentElement.style.overflow = "";
});
</script>

<style>
html,
body {
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
}

#app,
body > div {
  width: 100%;
  max-width: 100%;
  overflow-x: clip;
}

@supports not (overflow-x: clip) {
  #app,
  body > div {
    overflow-x: hidden;
  }
}

.hero-overlay {
  background: linear-gradient(
    135deg,
    rgba(91, 65, 93, 0.78) 0%,
    rgba(91, 65, 93, 0.58) 35%,
    rgba(122, 181, 156, 0.45) 100%
  );
}

@keyframes fadeInUp {
  0% {
    opacity: 0;
    transform: translateY(14px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in-up {
  animation: fadeInUp 900ms ease-out 1 forwards;
}

.article-prose {
  color: #141414;
}

.article-prose p {
  margin: 0 0 16px;
  line-height: 2;
  font-size: 17px;
}

.article-prose h2 {
  font-size: 28px;
  font-weight: 900;
  margin: 22px 0 12px;
}

.article-prose h3 {
  font-size: 22px;
  font-weight: 900;
  margin: 20px 0 10px;
}

.article-prose ul,
.article-prose ol {
  padding-left: 20px;
  margin: 12px 0 16px;
}

.article-prose li {
  margin: 6px 0;
}

.article-prose blockquote {
  margin: 16px 0;
  padding: 14px 16px;
  border-left: 4px solid rgba(91, 65, 93, 0.65);
  background: rgba(0, 0, 0, 0.06);
  border-radius: 18px;
}

.article-prose a {
  text-decoration: underline;
}

::-webkit-scrollbar {
  display: none;
}
</style>
